/**
 * Google Maps Route Distance & Travel Timing Calculation Service
 * 
 * Accurately calculates real-world driving distance (km) and estimated travel duration (timing)
 * between pickup (FROM) and drop-off (TO) locations.
 * 
 * Supports:
 * 1. Google Maps DistanceMatrixService / DirectionsService when API is active.
 * 2. High-precision live European road routing engine (OSRM) as fast resilient fallback.
 */

import { loadGoogleMapsScript } from './googleMapsPlaces'

export interface RouteCalculationResult {
  distanceKm: number
  distanceText: string
  durationMinutes: number
  durationText: string
  isCalculated: boolean
}

export interface RoutePoint {
  title: string
  address?: string
  lat?: number
  lng?: number
}

/**
 * Formats duration in minutes to human-readable timing (e.g. "3 hrs 15 mins", "45 mins")
 */
export function formatDrivingDuration(totalMinutes: number): string {
  if (!totalMinutes || totalMinutes <= 0) return '0 mins'
  
  const hours = Math.floor(totalMinutes / 60)
  const mins = totalMinutes % 60

  if (hours === 0) {
    return `${mins} min${mins === 1 ? '' : 's'}`
  }

  if (mins === 0) {
    return `${hours} hr${hours === 1 ? '' : 's'}`
  }

  return `${hours} hr${hours === 1 ? '' : 's'} ${mins} min${mins === 1 ? '' : 's'}`
}

/**
 * Helper to geocode a location string if coordinates are missing.
 */
async function geocodePointIfMissing(point: RoutePoint): Promise<{ lat: number; lng: number } | null> {
  if (typeof point.lat === 'number' && typeof point.lng === 'number' && !isNaN(point.lat) && !isNaN(point.lng)) {
    return { lat: point.lat, lng: point.lng }
  }

  const query = point.address || point.title
  if (!query) return null

  try {
    const res = await fetch(
      `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=1`
    )
    if (res.ok) {
      const data = await res.json()
      const first = data?.features?.[0]
      if (first?.geometry?.coordinates) {
        return {
          lng: first.geometry.coordinates[0],
          lat: first.geometry.coordinates[1],
        }
      }
    }
  } catch {
    // Silent fallback
  }

  return null
}

/**
 * Calculates road driving distance and timing between two locations according to Google Maps calculations.
 */
export async function calculateRouteDistanceAndTiming(
  origin: RoutePoint,
  destination: RoutePoint
): Promise<RouteCalculationResult> {
  const originStr = (origin.address || origin.title || '').trim()
  const destStr = (destination.address || destination.title || '').trim()

  if (!originStr || !destStr) {
    return {
      distanceKm: 0,
      distanceText: '0 km',
      durationMinutes: 0,
      durationText: '0 mins',
      isCalculated: false,
    }
  }

  // 1. Attempt Google Maps Distance Matrix Service if loaded
  await loadGoogleMapsScript()

  if (window.google?.maps?.DistanceMatrixService) {
    try {
      const distanceService = new window.google.maps.DistanceMatrixService()

      const originParam =
        typeof origin.lat === 'number' && typeof origin.lng === 'number'
          ? new window.google.maps.LatLng(origin.lat, origin.lng)
          : originStr

      const destParam =
        typeof destination.lat === 'number' && typeof destination.lng === 'number'
          ? new window.google.maps.LatLng(destination.lat, destination.lng)
          : destStr

      const result: any = await new Promise((resolve) => {
        distanceService.getDistanceMatrix(
          {
            origins: [originParam],
            destinations: [destParam],
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.METRIC,
          },
          (response: any, status: any) => {
            if (status === 'OK' && response?.rows?.[0]?.elements?.[0]?.status === 'OK') {
              resolve(response.rows[0].elements[0])
            } else {
              resolve(null)
            }
          }
        )
      })

      if (result?.distance && result?.duration) {
        const distanceKm = Math.max(1, Math.round(result.distance.value / 1000))
        const durationMinutes = Math.max(1, Math.round(result.duration.value / 60))

        return {
          distanceKm,
          distanceText: result.distance.text || `${distanceKm} km`,
          durationMinutes,
          durationText: result.duration.text || formatDrivingDuration(durationMinutes),
          isCalculated: true,
        }
      }
    } catch (err) {
      console.warn('Google Maps Distance Matrix query error, using live router:', err)
    }
  }

  // 2. High-precision live European road routing (OSRM Driving Engine)
  try {
    const originCoords = await geocodePointIfMissing(origin)
    const destCoords = await geocodePointIfMissing(destination)

    if (originCoords && destCoords) {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 4500)

      const url = `https://router.project-osrm.org/route/v1/driving/${originCoords.lng},${originCoords.lat};${destCoords.lng},${destCoords.lat}?overview=false`
      const res = await fetch(url, { signal: controller.signal })
      clearTimeout(timeout)

      if (res.ok) {
        const data = await res.json()
        const primaryRoute = data?.routes?.[0]

        if (primaryRoute && typeof primaryRoute.distance === 'number') {
          const distanceKm = Math.max(1, Math.round(primaryRoute.distance / 1000))
          const durationMinutes = Math.max(1, Math.round((primaryRoute.duration || 0) / 60))

          return {
            distanceKm,
            distanceText: `${distanceKm} km`,
            durationMinutes,
            durationText: formatDrivingDuration(durationMinutes),
            isCalculated: true,
          }
        }
      }
    }
  } catch (err) {
    console.warn('Live router calculation error:', err)
  }

  // 3. Fallback estimation based on coordinates distance formula if remote APIs are offline
  if (origin.lat && origin.lng && destination.lat && destination.lng) {
    const R = 6371 // Earth radius in km
    const dLat = ((destination.lat - origin.lat) * Math.PI) / 180
    const dLng = ((destination.lng - origin.lng) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((origin.lat * Math.PI) / 180) *
        Math.cos((destination.lat * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const straightDist = R * c

    // European driving road factor (~1.28x straight-line distance)
    const distanceKm = Math.max(5, Math.round(straightDist * 1.28))
    // Average 85 km/h driving speed
    const durationMinutes = Math.max(10, Math.round((distanceKm / 85) * 60))

    return {
      distanceKm,
      distanceText: `${distanceKm} km`,
      durationMinutes,
      durationText: formatDrivingDuration(durationMinutes),
      isCalculated: true,
    }
  }

  // Default fallback for empty or unresolvable locations
  return {
    distanceKm: 80,
    distanceText: '80 km',
    durationMinutes: 60,
    durationText: '1 hr',
    isCalculated: false,
  }
}
